import React, { forwardRef, useImperativeHandle, useState, useMemo } from "react";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import DataGrid from "devextreme-react/data-grid";
// Open Requisitions / PO Receipts still reuse the OM lot-keyed result grids (already bound to those endpoints).
import OpenRequisitionsTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/OpenRequisitionsTabResult";
import OpenPosReceiptTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/OpenPosReceiptTabResult";
import LotTab from "../../../im/codes/itemDataEntry/tabs/LotTab";
import { GetOrderDetailGridData } from "../../../om/services/SalesOrderService";
import {
  GetItemTransactionsGridData,
  GetTransactionHeaderGridData,
  GetVendorCrossReferenceGridData,
  GetOpenPosGridData,
  GetOmTransactionsGridData,
  GetLotLocationsGridData,
} from "../services/ItemManagementService";
import { ITEM_TRANSACTION_COLUMNS, VENDOR_CR_COLUMNS, TRANSACTION_HEADER_COLUMNS } from "../itemMasterTab/subTabColumns";
import { OPEN_PO_COLUMNS, OM_TRANSACTION_COLUMNS, LOT_LOCATION_COLUMNS } from "./lotSubTabColumns";
import apiRoutes from "../../../assets/apiRoutes.json";

// Reused OM endpoint routes, lot-keyed (Open Requisitions / PO Receipts).
const R = apiRoutes.OM.SaleOrder;

// Shared read-only sub-grid (same look/height/paging as the transactions bottom sub-grids).
const SubGrid = ({ data, columns }) => (
  <div style={{ height: 305 }}>
    <DataGrid
      dataSource={data}
      columns={columns}
      remoteOperations={true}
      showBorders={true}
      columnAutoWidth={true}
      height="100%"
      sorting={{ mode: "multiple" }}
      filterRow={{ visible: true }}
      headerFilter={{ visible: true }}
      columnChooser={{ enabled: true }}
      export={{ enabled: true }}
      paging={{ pageSize: 20 }}
      pager={{ visible: true, showPageSizeSelector: true, allowedPageSizes: [20, 50, 100], showInfo: true }}
    />
  </div>
);

const noSelection = (
  <div style={{ padding: 16, color: "#888" }}>Select an item/lot above to load this tab.</div>
);

// Bottom sub-tabs for the Item/Lot top tab (Delphi IMD0046). The scrolled row is a lot, so `rowid` = the lot
// rowid and `itemId` = its parent item rowid. Lot-keyed tabs filter by lot (Delphi R_Lot); the Lot tab lists
// the item's lots (item-keyed). Open Transactions shows all; Historical Transactions is lot-filtered (Delphi).
const ItemLotSubTabs = forwardRef(({ accessToken }, ref) => {
  const [selectedRow, setSelectedRow] = useState(null);

  useImperativeHandle(ref, () => ({
    updateData(rowData) { setSelectedRow(rowData); },
  }));

  const itemId = selectedRow && selectedRow.itemId ? selectedRow.itemId : null;
  const lotId = selectedRow && selectedRow.rowid ? selectedRow.rowid : null;

  // Lot-keyed sub-tabs (Delphi filters by R_Lot).
  const itemTransStore = useMemo(() => (lotId ? GetItemTransactionsGridData(accessToken, { lotId }) : null), [lotId, accessToken]);
  const vendorCrStore = useMemo(() => (lotId ? GetVendorCrossReferenceGridData(accessToken, { lotId }) : null), [lotId, accessToken]);
  const openReqStore = useMemo(() => (lotId ? GetOrderDetailGridData(accessToken, R.GetOpenRequisitionsByLotId, lotId) : null), [lotId, accessToken]);
  const posStore = useMemo(() => (lotId ? GetOpenPosGridData(accessToken, lotId) : null), [lotId, accessToken]);
  const poReceiptStore = useMemo(() => (lotId ? GetOrderDetailGridData(accessToken, R.GetPosReceiptByLotId, lotId) : null), [lotId, accessToken]);
  const omStore = useMemo(() => (lotId ? GetOmTransactionsGridData(accessToken, lotId) : null), [lotId, accessToken]);
  const lotLocStore = useMemo(() => (lotId ? GetLotLocationsGridData(accessToken, lotId) : null), [lotId, accessToken]);
  // Open Transactions is always-on (show all). Historical Transactions is lot-filtered (Delphi) — only
  // historical headers with a detail line for the selected lot.
  const openTransStore = useMemo(() => GetTransactionHeaderGridData(accessToken, false), [accessToken]);
  const histTransStore = useMemo(() => (lotId ? GetTransactionHeaderGridData(accessToken, true, null, { lotId }) : null), [lotId, accessToken]);

  return (
    <TabPanel deferRendering={true} animationEnabled={false}>
      <TabItem title="Item Transactions">{lotId ? <SubGrid data={itemTransStore} columns={ITEM_TRANSACTION_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="Lot"><LotTab key={itemId || "no-selection"} rowId={itemId} includeAllStatuses={true} gridHeight={305} /></TabItem>
      <TabItem title="Vendor Cross Reference">{lotId ? <SubGrid data={vendorCrStore} columns={VENDOR_CR_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="Open Requisitions"><OpenRequisitionsTabResult data={openReqStore} /></TabItem>
      <TabItem title="Open PO's">{lotId ? <SubGrid data={posStore} columns={OPEN_PO_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="PO Receipts"><OpenPosReceiptTabResult data={poReceiptStore} /></TabItem>
      <TabItem title="OM Transactions">{lotId ? <SubGrid data={omStore} columns={OM_TRANSACTION_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="Lot/Locations">{lotId ? <SubGrid data={lotLocStore} columns={LOT_LOCATION_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="Open Transactions"><SubGrid data={openTransStore} columns={TRANSACTION_HEADER_COLUMNS} /></TabItem>
      <TabItem title="Historical Transactions">{lotId ? <SubGrid data={histTransStore} columns={TRANSACTION_HEADER_COLUMNS} /> : noSelection}</TabItem>
    </TabPanel>
  );
});

export default ItemLotSubTabs;
